import React, { useState, useEffect, useRef, useMemo } from 'react';
import config from '../config';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import '../components/ticketStyles.css';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import * as XLSX from 'xlsx';

const TicketList = ({ isAdmin, user }) => {
    console.log('Config:', config);
    console.log('TICKET_STATUSES:', config?.TICKET_STATUSES);

    const defaultStatus = 'NEW';
    const ticketStatuses = config?.TICKET_STATUSES || [defaultStatus];

    const location = useLocation();
    const [tickets, setTickets] = useState([]);
    const [newTicket, setNewTicket] = useState({
        ticketTitle: '',
        description: '',
        ticketType: 'General Inquiry',
        status: 'NEW',
    });
    const [showModal, setShowModal] = useState(false);
    const [comments, setComments] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [notification, setNotification] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [ticketsPerPage] = useState(5);
    const [selectedDescription, setSelectedDescription] = useState('');
    const dialogRef = useRef(null);
    const [ticketTypes, setTicketTypes] = useState([]);
    const [editingTicket, setEditingTicket] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    useEffect(() => {
        fetchTickets();
        fetchTicketTypes();
    }, []);

    const showNotification = (message) => {
        setNotification(message);
        setTimeout(() => setNotification(''), 3000);
    };

    const fetchTickets = async () => {
        try {
            const apiUrl = config.API_BASE_URL;
            const response = await axios.get(`${apiUrl}/Tickets`);
            setTickets(response.data); // Set tickets from response
            showNotification('Tickets fetched successfully');
        } catch (error) {
            console.error('Error fetching tickets:', error);
            showNotification('Failed to fetch tickets', 'error');
        }
    };

    const fetchTicketTypes = async () => {
        try {
            const response = await axios.get(`${config.API_BASE_URL}/TicketTypes`);
            setTicketTypes(response.data);
        } catch (error) {
            console.error('Error fetching ticket types:', error);
        }
    };

    const handleTypeChange = async (ticketId, newType) => {
        try {
            await axios.patch(`${config.API_BASE_URL}/Tickets/${ticketId}`, { type: newType });
            fetchTickets(); // Refresh the ticket list
            setEditingTicket(null);
        } catch (error) {
            console.error('Error updating ticket type:', error);
        }
    };

    const addTicket = async (e) => {
        e.preventDefault(); // Prevent default form submission
        try {
            const apiUrl = config.API_BASE_URL;
            const response = await axios.post(`${apiUrl}/Tickets`, {
                title: newTicket.ticketTitle,
                description: newTicket.description, // This will now contain HTML content
                type: newTicket.ticketType,
                status: newTicket.status,
                createdAt: new Date().toISOString(),
                createdBy: user?.name || 'Unknown User',
                company: user?.company || 'Unknown Company', // Use the company from the user object
                email: user?.email || 'Unknown Email',
            }, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            if (response.status === 201) {
                fetchTickets(); // Refresh the ticket list
                setNewTicket({
                    ticketTitle: '',
                    description: '',
                    ticketType: 'General Inquiry',
                    status: ticketStatuses[0] || defaultStatus,
                });
                setShowModal(false); // Close the modal
                showNotification('Ticket added successfully');
            }
        } catch (error) {
            console.error('Error adding ticket:', error);
            showNotification('Failed to add ticket', 'error');
        }
    };

    const updateTicketStatusAndComment = async (ticket) => {
        try {
            const response = await axios.post(`${config.API_BASE_URL}/tickets/${ticket.id}`, {
                status: ticket.status,
                comment: comments[ticket.id]?.trim() || '',
                title: ticket.title,
                description: ticket.description,
                createdBy: ticket.createdBy,
                company: ticket.company,
                email: ticket.email,
                ticketNumber: ticket.ticketNumber,
                Type: ticket.type,
                id: ticket.id
            }, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            if (response.status === 200) {
                fetchTickets(); // Refresh the ticket list
                setComments((prev) => ({ ...prev, [ticket.id]: '' })); // Clear the comment input for this ticket
                showNotification('Ticket updated successfully');
            } else {
                console.error('Failed to update ticket:', response.data);
                showNotification('Failed to update ticket', 'error');
            }
        } catch (error) {
            console.error('Error updating ticket status and adding comment:', error);
            showNotification('Error updating ticket', 'error');
        }
    };

    // Filter tickets based on search query
    const filteredTickets = tickets.filter(ticket => {
        const searchLower = searchQuery.toLowerCase();
        return (
            ticket.title.toLowerCase().includes(searchLower) ||
            ticket.createdBy.toLowerCase().includes(searchLower) ||
            (ticket.ticketNumber && ticket.ticketNumber.toLowerCase().includes(searchLower))
        );
    });

    const sortedTickets = useMemo(() => {
        let sortableTickets = [...filteredTickets];
        if (sortConfig.key !== null) {
            sortableTickets.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableTickets;
    }, [filteredTickets, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const exportToExcel = () => {
        // Prepare the data for export
        const dataToExport = sortedTickets.map(ticket => ({
            'Ticket Number': ticket.ticketNumber,
            'Created By': ticket.createdBy,
            'Email': ticket.email,
            'Title': ticket.title,
            'Description': ticket.description.replace(/<[^>]+>/g, ''), // Remove HTML tags
            'Type': ticket.type,
            'Status': ticket.status,
            'Comment': ticket.comment,
            'Closed Date': ticket.closeddate ? new Date(ticket.closeddate).toLocaleDateString() : 'N/A',
            'Company': ticket.company
        }));

        // Create a new workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);

        // Auto-fit columns
        const colWidths = dataToExport.reduce((acc, row) => {
            Object.keys(row).forEach((key, i) => {
                const cellValue = row[key] ? row[key].toString() : '';
                acc[i] = Math.max(acc[i] || 0, cellValue.length);
            });
            return acc;
        }, {});

        worksheet['!cols'] = Object.keys(colWidths).map(i => ({ wch: colWidths[i] }));

        // Add the worksheet to the workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, "Tickets");

        // Generate Excel file and trigger download
        XLSX.writeFile(workbook, "tickets_export.xlsx");
    };

    // Get current tickets
    const indexOfLastTicket = currentPage * ticketsPerPage;
    const indexOfFirstTicket = indexOfLastTicket - ticketsPerPage;
    const currentTickets = sortedTickets.slice(indexOfFirstTicket, indexOfLastTicket);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const testNotification = () => {
        showNotification('This is a test notification');
        console.log('Test notification triggered');
    };

    const handleLogout = () => {
        // Implement logout functionality
        console.log('Logout clicked');
    };


    const isTicketResolved = (status) => {
        return status === 'CLOSED' || status === 'RESOLVED';
    };

    const openDescriptionDialog = (description) => {
        setSelectedDescription(description);
        dialogRef.current.showModal();
    };

    const closeDescriptionDialog = () => {
        dialogRef.current.close();
    };

    return (
        <>
            <div className="ticket-list">
                {notification && (
                    <div className="alert alert-info" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
                        {notification}
                    </div>
                )}
                <h2>Tickets</h2>
                <div className="search-add">
                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Search by title, creator, or ticket number"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                    </div>
                    <button onClick={() => setShowModal(true)}>Add Ticket</button>
                    <button onClick={exportToExcel}>Export to Excel</button>
                </div>

                <table>
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('ticketNumber')}>
                                Ticket Number {sortConfig.key === 'ticketNumber' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
                            </th>
                            <th onClick={() => requestSort('createdBy')}>
                                User {sortConfig.key === 'createdBy' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
                            </th>
                            <th onClick={() => requestSort('title')}>
                                Ticket {sortConfig.key === 'title' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
                            </th>
                            <th>Description</th>
                            <th onClick={() => requestSort('type')}>
                                Type {sortConfig.key === 'type' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
                            </th>
                            <th onClick={() => requestSort('status')}>
                                Status {sortConfig.key === 'status' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
                            </th>
                            <th>Comment</th>
                            <th onClick={() => requestSort('closeddate')}>
                                Closed Date {sortConfig.key === 'closeddate' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
                            </th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentTickets.map((ticket) => (
                            <tr key={ticket.id}>
                                <td>{ticket.ticketNumber}</td>
                                <td>
                                    <span>{ticket.createdBy}</span><br />
                                    <span>{ticket.email}</span>
                                </td>
                                <td><strong>{ticket.title}</strong></td>
                                <td>
                                    <div 
                                        className="description-cell" 
                                        onClick={() => openDescriptionDialog(ticket.description)}
                                        dangerouslySetInnerHTML={{ __html: ticket.description }}
                                    ></div>
                                </td>
                                <td>
                                    {isAdmin && editingTicket === ticket.id ? (
                                        <select
                                            value={ticket.type}
                                            onChange={(e) => handleTypeChange(ticket.id, e.target.value)}
                                            onBlur={() => setEditingTicket(null)}
                                        >
                                            {ticketTypes.map((type) => (
                                                <option key={type} value={type}>
                                                    {type}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span 
                                            className={isAdmin ? "editable-field" : ""}
                                            onClick={() => isAdmin && setEditingTicket(ticket.id)}
                                        >
                                            {ticket.type}
                                        </span>
                                    )}
                                </td>
                                <td>
                                    <span className={`status ${ticket.status.toLowerCase()}`}>
                                        {ticket.status}
                                    </span>
                                </td>
                                <td>{ticket.comment || 'No comments'}</td>
                                <td>{ticket.closeddate ? new Date(ticket.closeddate).toLocaleDateString() : 'N/A'}</td>
                                <td>
                                    {isAdmin ? (
                                        <>
                                            <select
                                                value={ticket.status}
                                                onChange={(e) => {
                                                    const updatedTicket = { ...ticket, status: e.target.value };
                                                    setTickets(tickets.map(t => t.id === ticket.id ? updatedTicket : t));
                                                }}
                                            >
                                                {ticketStatuses.map(status => (
                                                    <option key={status} value={status}>{status}</option>
                                                ))}
                                            </select>
                                            <input
                                                type="text"
                                                placeholder="Add a comment"
                                                value={comments[ticket.id] || ''}
                                                onChange={(e) => setComments((prev) => ({ ...prev, [ticket.id]: e.target.value }))}
                                            />
                                            <button 
                                                className="btn btn-update"
                                                onClick={() => updateTicketStatusAndComment(ticket)}
                                            >
                                                Update
                                            </button>
                                        </>
                                    ) : (
                                        'N/A'
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <Pagination
                    ticketsPerPage={ticketsPerPage}
                    totalTickets={filteredTickets.length}
                    paginate={paginate}
                    currentPage={currentPage}
                />
            </div>

            {showModal && (
                <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add New Ticket</h5>
                                <button type="button" className="close" onClick={() => setShowModal(false)} aria-label="Close">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={addTicket}>
                                    <div className="mb-3">
                                        <label className="form-label">Ticket Title</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newTicket.ticketTitle}
                                            onChange={(e) => setNewTicket({ ...newTicket, ticketTitle: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Description</label>
                                        <ReactQuill 
                                            theme="snow"
                                            value={newTicket.description}
                                            onChange={(content) => setNewTicket({ ...newTicket, description: content })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Ticket Type</label>
                                        <select
                                            className="form-select"
                                            value={newTicket.ticketType}
                                            onChange={(e) => setNewTicket({ ...newTicket, ticketType: e.target.value })}
                                        >
                                            <option value="General Inquiry">General Inquiry</option>
                                            <option value="Bug">Bug</option>
                                            <option value="Feature Request">Feature Request</option>
                                            <option value="Support">Support</option>
                                        </select>
                                    </div>
                                    <button type="submit" className="btn btn-primary">Add Ticket</button>
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Description Dialog */}
            <dialog ref={dialogRef} className="description-dialog">
                <div className="dialog-content">
                    <div className="dialog-header">
                        <h2>Ticket Description</h2>
                        <button className="btn btn-close" onClick={closeDescriptionDialog}>Close</button>
                    </div>
                    <div className="dialog-body" dangerouslySetInnerHTML={{ __html: selectedDescription }}></div>
                    <div className="dialog-footer">
                        <button onClick={closeDescriptionDialog}>Close</button>
                    </div>
                </div>
            </dialog>
        </>
    );
};

const Pagination = ({ ticketsPerPage, totalTickets, paginate, currentPage }) => {
    const pageNumbers = [];

    for (let i = 1; i <= Math.ceil(totalTickets / ticketsPerPage); i++) {
        pageNumbers.push(i);
    }

    return (
        <nav>
            <ul className='pagination'>
                {pageNumbers.map(number => (
                    <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                        <a onClick={() => paginate(number)} href='#!' className='page-link'>
                            {number}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
};

export default TicketList;